import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Hash, Users, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { playersService } from '../services/players';
import { MediaServices } from '../services/media.service';
import { MEDIA_BASE_URL } from '../lib/env';

const COLUMNS = [
  { header: 'Player Name', render: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() },
  { header: 'Email', accessor: 'email' },
  { header: 'Mobile', accessor: 'mobile_number' },
  { header: 'Role', accessor: 'role' },
  { header: 'Matches Played', accessor: 'matches' },
  { header: 'Status', render: (row) => <span style={{ textTransform: 'capitalize', color: row.status === 'active' ? 'green' : 'red' }}>{row.status}</span> }
];

const FORM_FIELDS = [
  { label: 'Player Photo', accessor: 'image', type: 'file', fullWidth: true },

  // Personal Details
  { label: 'First Name', accessor: 'first_name', placeholder: 'Enter first name' },
  { label: 'Middle Name', accessor: 'middle_name', placeholder: 'Enter middle name' },
  { label: 'Last Name', accessor: 'last_name', placeholder: 'Enter last name' },
  { label: 'Date of Birth', accessor: 'dob', type: 'date' },
  { label: 'Joining Date', accessor: 'joining_date', type: 'date' },
  { label: 'Medical History', accessor: 'medical_history', placeholder: 'Any medical conditions?' },

  // Contact & Address
  { label: 'Email', accessor: 'email', type: 'email', placeholder: 'Enter email address' },
  { label: 'Mobile Number', accessor: 'mobile_number', placeholder: 'Enter mobile number' },
  { label: 'Address Line 1', accessor: 'address_line_1', placeholder: 'Street address' },
  { label: 'Area', accessor: 'area', placeholder: 'Locality/Area' },
  { label: 'Taluka', accessor: 'taluka', placeholder: 'Taluka' },
  { label: 'District', accessor: 'district', placeholder: 'District' },
  {
    label: 'State', accessor: 'state', type: 'select', options: [
      { label: 'Andhra Pradesh', value: 'Andhra Pradesh' },
      { label: 'Arunachal Pradesh', value: 'Arunachal Pradesh' },
      { label: 'Assam', value: 'Assam' },
      { label: 'Bihar', value: 'Bihar' },
      { label: 'Chhattisgarh', value: 'Chhattisgarh' },
      { label: 'Goa', value: 'Goa' },
      { label: 'Gujarat', value: 'Gujarat' },
      { label: 'Haryana', value: 'Haryana' },
      { label: 'Himachal Pradesh', value: 'Himachal Pradesh' },
      { label: 'Jharkhand', value: 'Jharkhand' },
      { label: 'Karnataka', value: 'Karnataka' },
      { label: 'Kerala', value: 'Kerala' },
      { label: 'Madhya Pradesh', value: 'Madhya Pradesh' },
      { label: 'Maharashtra', value: 'Maharashtra' },
      { label: 'Manipur', value: 'Manipur' },
      { label: 'Meghalaya', value: 'Meghalaya' },
      { label: 'Mizoram', value: 'Mizoram' },
      { label: 'Nagaland', value: 'Nagaland' },
      { label: 'Odisha', value: 'Odisha' },
      { label: 'Punjab', value: 'Punjab' },
      { label: 'Rajasthan', value: 'Rajasthan' },
      { label: 'Sikkim', value: 'Sikkim' },
      { label: 'Tamil Nadu', value: 'Tamil Nadu' },
      { label: 'Telangana', value: 'Telangana' },
      { label: 'Tripura', value: 'Tripura' },
      { label: 'Uttar Pradesh', value: 'Uttar Pradesh' },
      { label: 'Uttarakhand', value: 'Uttarakhand' },
      { label: 'West Bengal', value: 'West Bengal' }
    ]
  },

  // Cricket Profile
  {
    label: 'Role', accessor: 'role', type: 'select', options: [
      { label: 'Batsman', value: 'Batsman' },
      { label: 'Bowler', value: 'Bowler' },
      { label: 'All-rounder', value: 'All-rounder' },
      { label: 'Wicket-keeper', value: 'Wicket-keeper' }
    ]
  },
  {
    label: 'Batting Style', accessor: 'batting_style', type: 'select', options: [
      { label: 'Right-hand bat', value: 'Right-hand bat' },
      { label: 'Left-hand bat', value: 'Left-hand bat' }
    ]
  },
  {
    label: 'Bowling Style', accessor: 'bowling_style', type: 'select', options: [
      { label: 'None', value: 'None' },
      { label: 'Right-arm fast', value: 'Right-arm fast' },
      { label: 'Right-arm medium', value: 'Right-arm medium' },
      { label: 'Right-arm offbreak', value: 'Right-arm offbreak' },
      { label: 'Right-arm legbreak', value: 'Right-arm legbreak' },
      { label: 'Left-arm fast', value: 'Left-arm fast' },
      { label: 'Left-arm medium', value: 'Left-arm medium' },
      { label: 'Left-arm orthodox', value: 'Left-arm orthodox' },
      { label: 'Left-arm chinaman', value: 'Left-arm chinaman' }
    ]
  },
  { label: 'Current Rank', accessor: 'rank', placeholder: 'Enter national rank', type: 'number' },
  { label: 'Total Matches', accessor: 'matches', placeholder: 'Total tennis cricket matches', type: 'number' },
  { label: 'Penalty', accessor: 'penalty', placeholder: 'Any penalties?' },
  {
    label: 'Status', accessor: 'status', type: 'select', options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'Suspended', value: 'suspended' }
    ]
  },
  { label: 'Player Bio', accessor: 'biography', type: 'textarea', placeholder: 'Career highlights...', fullWidth: true }
];

export default function Players() {
  const navigate = useNavigate();
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Search & Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 5;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await playersService.getAll();
      const list = Array.isArray(res) ? res : (res?.data || []);
      const formattedList = list.map(item => ({
        ...item,
        id: item._id || item.id,
      }));
      setDataList(formattedList);
    } catch (error) {
      toast.error('Failed to load players');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);



  const filteredData = useMemo(() => {
    let result = dataList;
    if (debouncedQuery) {
      result = result.filter(item =>
        Object.values(item).some(val =>
          String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
        )
      );
    }
    return result;
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    return [
      { label: "Total Registered", value: total, icon: <Users size={20} />, color: 'blue' },
      { label: "Top Ranker", value: dataList.find(d => d.rank === '1')?.name || "N/A", icon: <Star size={20} />, color: 'orange' },
      { label: "Total States", value: new Set(dataList.map(d => d.state).filter(Boolean)).size, icon: <Hash size={20} />, color: 'green' }
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFormSubmit = async (formData) => {
    try {
      let imagePath = editingData?.original_image || editingData?.image;
      let imageId = null;
      if (formData.image_file) {
        toast.loading("Uploading photo...", { id: "uploadToast" });
        const mediaRes = await MediaServices.uploadMedia(formData.image_file, "image", "player");
        toast.dismiss("uploadToast");
        if (mediaRes?.success && mediaRes?.data) {
          imageId = mediaRes.data.id;
          imagePath = mediaRes.data.name;
        } else {
          toast.error("Photo upload failed. Please try again.");
          return;
        }
      }

      const payload = { ...formData };
      if (imagePath) {
        payload.image_id = imageId;
      }
      delete payload.image_file;
      delete payload.original_image;

      if (editingData) {
        const targetId = editingData._id || editingData.id;
        const res = await playersService.update(targetId, payload);
        const updatedItem = { ...(res?.data || payload), id: targetId, image: imagePath };
        setDataList(prev => prev.map(item => item.id === targetId ? updatedItem : item));
        toast.success('Player profile updated successfully!');
      } else {
        const res = await playersService.create(payload);
        const newItem = { ...(res?.data || payload), id: res?.data?._id || Date.now(), image: imagePath };
        setDataList(prev => [newItem, ...prev]);
        toast.success('Player profile added successfully!');
      }
      setIsFormOpen(false);
      setEditingData(null);
      fetchData();
    } catch (error) {
      toast.dismiss("uploadToast");
      toast.error(error.response?.data?.message || 'Failed to save player profile');
    }
  };

  const handleEdit = (row) => {
    setEditingData({
      ...row,
      image: row.image ? `${MEDIA_BASE_URL}/${row.image}` : null,
      original_image: row.image
    });
    setIsFormOpen(true);
  };

  const handleView = (row) => {
    const targetId = row._id || row.id;
    navigate(`/players/${targetId}`);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    try {
      const targetId = deleteTarget._id || deleteTarget.id;
      await playersService.delete(targetId);
      setDataList(prev => prev.filter(item => item.id !== targetId));
      toast.success('Player deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete player');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Player Profiles"
        subtitle="Manage national rankings and player history profiles"
        onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
        addLabel="Add Player"
        kpis={kpis}
      />

      {isFormOpen && (
        <DataForm
          title={editingData ? `Edit Player Profile` : `Add New Player Profile`}
          fields={FORM_FIELDS}
          initialData={editingData}
          onSubmit={handleFormSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      )}

      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar
              placeholder="Search players..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable
            columns={COLUMNS}
            data={paginatedData}
            onEdit={handleEdit}
            onView={handleView}
            // onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Player Profile"
        message="Are you sure you want to delete this player's official profile?"
        itemName={deleteTarget ? deleteTarget.name : ''}
      />
    </div>
  );
}
